import React from 'react';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded';

/**
 * EmptyState — Reusable empty state component for list views.
 * Shows friendly message when no data is available.
 *
 * @param {string} title - Main message (e.g., "No products found")
 * @param {string} description - Secondary message explaining why (optional)
 * @param {React.ReactNode} icon - Icon to display (optional, defaults to folder)
 * @param {React.ReactNode} action - Action button (optional)
 */
const EmptyState = ({
  title = 'No data found',
  description = 'Nothing to display here yet.',
  icon: Icon = FolderOpenRoundedIcon,
  action = null,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'grid',
        placeItems: 'center',
        minHeight: 300,
        py: 4,
        px: 2,
        borderRadius: 2,
        background: isDark
          ? alpha(theme.palette.primary.main, 0.05)
          : alpha(theme.palette.primary.main, 0.02),
        border: `1px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: -42,
          right: -42,
          width: 120,
          height: 120,
          borderRadius: '50%',
          background: alpha(theme.palette.primary.main, isDark ? 0.14 : 0.1),
          filter: 'blur(2px)'
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -34,
          left: -34,
          width: 96,
          height: 96,
          borderRadius: '50%',
          background: alpha(theme.palette.secondary.main, isDark ? 0.12 : 0.08),
          filter: 'blur(2px)'
        }}
      />
      <Stack spacing={2} alignItems="center">
        <Icon
          sx={{
            fontSize: 64,
            color: theme.palette.primary.main,
            opacity: 0.5,
            animation: 'empty-icon-float 2.2s ease-in-out infinite',
            '@keyframes empty-icon-float': {
              '0%, 100%': { transform: 'translateY(0px)' },
              '50%': { transform: 'translateY(-3px)' }
            }
          }}
        />
        <Stack spacing={0.5} alignItems="center">
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              maxWidth: 400,
              textAlign: 'center',
            }}
          >
            {description}
          </Typography>
        </Stack>
        {action && <Box sx={{ mt: 1 }}>{action}</Box>}
      </Stack>
    </Box>
  );
};

export default EmptyState;
